
import { useState } from 'react';

interface Technology {
  id: number;
  title: string;
  description: string;
  icon: string;
  features: string[];
  specifications?: { [key: string]: string };
}

export default function TechnologyShowcase() {
  const [activeTech, setActiveTech] = useState<number>(1);

  const technologies: Technology[] = [
    {
      id: 1,
      title: 'Yuga-1 Heavy-Lift Launch Vehicle',
      description: 'Revolutionary heavy-lift launch vehicle with 4,000 kg payload capacity to LEO, featuring advanced hybrid propulsion systems.',
      icon: 'ri-rocket-line',
      features: [
        '4,000 kg payload capacity to Low Earth Orbit',
        'Advanced hybrid propulsion with AI integration',
        'Adaptive cycle engines with stealth features',
        'Scalable launch system for multiple mission types'
      ],
      specifications: {
        'Payload Capacity': '4,000 kg to LEO',
        'Engine Type': 'Hybrid Adaptive Cycle',
        'Thrust Vectoring': '3D Control System',
        'AI Integration': 'Autonomous Flight Control'
      }
    },
    {
      id: 2,
      title: 'Rudravayu-M Adaptive Engine',
      description: 'Advanced adaptive cycle engine with AI integration, stealth optimization, and ramjet booster channel for multi-mission adaptability.',
      icon: 'ri-fire-line',
      features: [
        'Adaptive cycle core with variable geometry',
        'High-temperature turbine stages for efficiency',
        'Integrated AI sensor module for real-time control',
        'Stealth-optimized nozzle design'
      ],
      specifications: {
        'Engine Type': 'Adaptive Cycle Hybrid',
        'AI Integration': 'Real-time Sensor Module',
        'Stealth Features': 'Optimized Nozzle Design',
        'Applications': 'Fighter Jets & UAVs'
      }
    },
    {
      id: 3,
      title: 'Agnivega-X Hypersonic Engine',
      description: 'High-performance engine with 3D thrust vectoring, AI sensor integration, and ramjet booster for hypersonic applications.',
      icon: 'ri-flashlight-line',
      features: [
        'High-pressure compressor stages for maximum thrust',
        '3D thrust vectoring control system',
        'AI sensor module for autonomous operation',
        'Flame stabilizers for hypersonic flight'
      ],
      specifications: {
        'Thrust Control': '3D Vectoring System',
        'Compressor': 'High-Pressure Multi-Stage',
        'Speed Range': 'Hypersonic Capable',
        'AI Features': 'Autonomous Flight Control'
      }
    },
    {
      id: 4,
      title: 'Vyoamdristi-1 AI Satellite',
      description: 'AI-integrated satellite system for real-time Earth observation, analytics, and advanced thermal imaging capabilities.',
      icon: 'ri-satellite-line',
      features: [
        'Real-time Earth observation and analytics',
        'AI-powered thermal imaging and data processing',
        'Climate monitoring and CO₂ tracking systems',
        'Disaster prediction and emergency response'
      ],
      specifications: {
        'AI Processing': 'Onboard Analytics',
        'Imaging': 'Advanced Thermal Sensors',
        'Applications': 'Climate & Disaster Monitoring',
        'Data Processing': 'Real-time AI Analysis'
      }
    },
    {
      id: 5,
      title: 'Vajrakrit-7 Multi-Mission Engine',
      description: 'Advanced engine with triple thrust-vectoring nozzles, adaptive cycle core, and AI sensor integration for versatile applications.',
      icon: 'ri-speed-line',
      features: [
        'Triple thrust-vectoring nozzles for precision control',
        'Adaptive cycle core for efficiency optimization',
        'High-temperature turbines for extreme conditions',
        'AI sensor module for autonomous operation'
      ],
      specifications: {
        'Nozzles': '3 Thrust-Vectoring Systems',
        'Core Type': 'Adaptive Cycle',
        'Temperature Rating': 'High-Temp Turbines',
        'Control System': 'AI-Integrated Sensors'
      }
    }
  ];

  return (
    <section className="py-20 bg-black">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Advanced Propulsion & Technologies
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Discover Vyoamyug's revolutionary aerospace technologies designed by RVH Dadhich, 
            featuring hybrid adaptive cycle engines, AI integration, and stealth optimization
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Technology Cards */}
          <div className="space-y-6">
            {technologies.map((tech) => (
              <div
                key={tech.id}
                className={`p-6 rounded-xl border cursor-pointer transition-all duration-300 ${
                  activeTech === tech.id
                    ? 'bg-blue-600/20 border-blue-400/50 shadow-lg shadow-blue-500/25'
                    : 'bg-gray-900/50 border-gray-800 hover:bg-gray-900/70'
                }`}
                onClick={() => setActiveTech(tech.id)}
              >
                <div className="flex items-start space-x-4">
                  <div className={`w-12 h-12 flex items-center justify-center rounded-lg transition-all duration-300 ${
                    activeTech === tech.id ? 'bg-blue-600 animate-pulse' : 'bg-gray-800'
                  }`}>
                    <i className={`${tech.icon} text-xl text-white`}></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-white mb-2">{tech.title}</h3>
                    <p className="text-gray-400 mb-4">{tech.description}</p>
                    
                    {activeTech === tech.id && (
                      <div className="space-y-3 animate-fadeIn">
                        <div className="grid grid-cols-2 gap-3 mb-4">
                          {tech.specifications && Object.entries(tech.specifications).map(([key, value], index) => (
                            <div key={index} className="bg-gray-800/50 rounded-lg p-3 border border-gray-700 hover:border-blue-400/30 transition-colors duration-300">
                              <div className="text-blue-400 text-xs font-medium">{key}</div>
                              <div className="text-white text-sm">{value}</div>
                            </div>
                          ))}
                        </div>
                        {tech.features.map((feature, index) => (
                          <div key={index} className="flex items-center space-x-3">
                            <i className="ri-check-line text-blue-400 text-sm animate-bounce"></i>
                            <span className="text-gray-300 text-sm">{feature}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Technology Visualization */}
          <div className="relative">
            {technologies.map((tech) => (
              activeTech === tech.id && (
                <div key={tech.id} className="relative rounded-2xl group">
                  <div className="w-full h-96 bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl border border-gray-700 flex items-center justify-center group-hover:border-blue-400/50 transition-all duration-300">
                    <div className="w-40 h-40 flex items-center justify-center bg-gradient-to-r from-blue-600 to-purple-600 rounded-full group-hover:scale-110 transition-transform duration-500 animate-pulse">
                      <i className={`${tech.icon} text-7xl text-white group-hover:animate-spin`} style={{animationDuration: '3s'}}></i>
                    </div>
                  </div>
                  
                  {/* Floating Elements */}
                  <div className="absolute top-4 right-4 animate-bounce">
                    <div className="bg-black/50 backdrop-blur-sm rounded-lg px-3 py-2 border border-blue-400/30">
                      <span className="text-blue-400 text-sm font-medium">RVH Dadhich Design</span>
                    </div>
                  </div>
                  
                  {/* Tech Stats */}
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="bg-black/50 backdrop-blur-sm rounded-lg p-4 border border-gray-700">
                      <h4 className="text-white font-semibold mb-2">{tech.title}</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="text-center">
                          <div className="text-blue-400 text-2xl font-bold animate-pulse">2024+</div>
                          <div className="text-gray-300 text-xs">Development Phase</div>
                        </div>
                        <div className="text-center">
                          <div className="text-blue-400 text-2xl font-bold animate-pulse">AI-Powered</div>
                          <div className="text-gray-300 text-xs">Intelligence Level</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )
            ))}
          </div>
        </div>

        {/* Engine Portfolio Overview */}
        <div className="mt-20">
          <div className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-2xl p-8 border border-blue-400/30">
            <h3 className="text-2xl font-bold text-white mb-6 text-center">Complete Engine Portfolio</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-gray-800/50 rounded-lg p-6 text-center group hover:bg-gray-700/50 transition-all duration-300 border border-gray-700 hover:border-blue-400/50">
                <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 animate-pulse">
                  <i className="ri-fire-line text-2xl text-white group-hover:animate-spin" style={{animationDuration: '2s'}}></i>
                </div>
                <h4 className="text-blue-400 font-semibold mb-2">Rudravayu-M</h4>
                <p className="text-gray-300 text-sm">Adaptive cycle, stealth-optimized, AI-integrated</p>
              </div>
              <div className="bg-gray-800/50 rounded-lg p-6 text-center group hover:bg-gray-700/50 transition-all duration-300 border border-gray-700 hover:border-blue-400/50">
                <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 animate-pulse">
                  <i className="ri-flashlight-line text-2xl text-white group-hover:animate-bounce"></i>
                </div>
                <h4 className="text-blue-400 font-semibold mb-2">Agnivega-X</h4>
                <p className="text-gray-300 text-sm">3D thrust vectoring, hypersonic capable</p>
              </div>
              <div className="bg-gray-800/50 rounded-lg p-6 text-center group hover:bg-gray-700/50 transition-all duration-300 border border-gray-700 hover:border-blue-400/50">
                <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 animate-pulse">
                  <i className="ri-thunder-line text-2xl text-white group-hover:animate-pulse"></i>
                </div>
                <h4 className="text-blue-400 font-semibold mb-2">Vyomshakti-X</h4>
                <p className="text-gray-300 text-sm">Ceramic matrix turbines, bypass channel</p>
              </div>
              <div className="bg-gray-800/50 rounded-lg p-6 text-center group hover:bg-gray-700/50 transition-all duration-300 border border-gray-700 hover:border-blue-400/50">
                <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 animate-pulse">
                  <i className="ri-speed-line text-2xl text-white group-hover:animate-spin" style={{animationDuration: '1.5s'}}></i>
                </div>
                <h4 className="text-blue-400 font-semibold mb-2">Vajrakrit-7</h4>
                <p className="text-gray-300 text-sm">Triple vectoring nozzles, multi-mission</p>
              </div>
              <div className="bg-gray-800/50 rounded-lg p-6 text-center group hover:bg-gray-700/50 transition-all duration-300 border border-gray-700 hover:border-blue-400/50">
                <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 animate-pulse">
                  <i className="ri-settings-3-line text-2xl text-white group-hover:animate-spin"></i>
                </div>
                <h4 className="text-blue-400 font-semibold mb-2">Hybrid Concepts</h4>
                <p className="text-gray-300 text-sm">Variable geometry, modular design</p>
              </div>
              <div className="bg-gray-800/50 rounded-lg p-6 text-center group hover:bg-gray-700/50 transition-all duration-300 border border-gray-700 hover:border-blue-400/50">
                <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 animate-pulse">
                  <i className="ri-rocket-2-line text-2xl text-white group-hover:animate-bounce"></i>
                </div>
                <h4 className="text-blue-400 font-semibold mb-2">Applications</h4>
                <p className="text-gray-300 text-sm">Fighter jets, UAVs, hypersonics</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
